import { Resources } from 'constants/resources';

import { MerchantNotificationsEventNamesType } from 'config/requests/merchants';
import { usePermissions } from 'hooks/usePermissions';
import {
  MerchantsShowNotificationsForm,
  MerchantsShowNotificationsFormValues,
} from 'pages/Merchants/MerchantsShow/MerchantsShowNotifications/components/MerchantsShowNotificationsForm/MerchantsShowNotificationsForm';
import { useEditMerchantNotification } from 'pages/Merchants/MerchantsShow/MerchantsShowNotifications/hooks/useEditMerchantNotification';
import { getMerchantNotificationData } from 'pages/Merchants/MerchantsShow/MerchantsShowNotifications/utils/getMerchantNotificationData';
import { getMerchantNotificationFormValues } from 'pages/Merchants/MerchantsShow/MerchantsShowNotifications/utils/getMerchantNotificationFormValues';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { queryClient } from 'providers/queryClient';
import { Form, RecordContextProvider } from 'react-admin';
import { FieldValues } from 'react-hook-form';
import { Button, DeleteButton, SaveButton } from 'shared/mui/Buttons';
import { CardActionsButtons } from 'shared/mui/Card';
import { CardHeader } from 'shared/mui/Card/CardHeader/CardHeader';
import { Dialog } from 'shared/mui/Dialogs';
import { CloseIconButton } from 'shared/mui/IconButtons';
import { MerchantNotification } from 'types/embedded/merchantNotification';
import { Permission } from 'types/embedded/permission';

type Props = {
  open: boolean;
  onClose: VoidFunction;
  onDelete: (record: MerchantNotification) => void;
  record: MerchantNotification;
  merchantId: string;
  eventNames?: MerchantNotificationsEventNamesType[];
};

export const MerchantsShowNotificationsEditDialog: React.FC<Props> = ({
  open,
  onClose,
  onDelete,
  merchantId,
  record,
  eventNames,
}) => {
  const translate = useTypedTranslate();

  const { hasResourcePermissions } = usePermissions();
  const { isLoading, mutate } = useEditMerchantNotification(record.id, () => {
    onClose();
    queryClient.invalidateQueries({ queryKey: ['merchant_notification_settings'] });
  });

  const handleSubmit = (formData: MerchantsShowNotificationsFormValues) => {
    const data = getMerchantNotificationData(formData, merchantId, eventNames);
    mutate(data);
  };

  return (
    <Dialog maxWidth="md" onClose={onClose} open={open}>
      <RecordContextProvider value={getMerchantNotificationFormValues(record, eventNames)}>
        <Form
          onSubmit={(data: FieldValues) =>
            handleSubmit(data as MerchantsShowNotificationsFormValues)
          }
        >
          <CardHeader
            appendActions={[<CloseIconButton key="close-button" onClose={onClose} />]}
            title={translate('app.merchantsShowNotifications.editNotificationSettings')}
            titleSize="smallBold"
          />
          <MerchantsShowNotificationsForm eventNames={eventNames} />
          <CardActionsButtons
            leftActionsSlot={
              hasResourcePermissions(Resources.MerchantNotificationSettings, [
                Permission.Full,
                Permission.Delete,
              ]) && (
                <DeleteButton
                  onClick={() => {
                    onClose();
                    record && onDelete(record);
                  }}
                  variant="outlined"
                />
              )
            }
            rightActionsSlot={[
              <Button
                key="cancel"
                label={translate('app.buttons.cancel')}
                onClick={onClose}
                variant="outlined"
              />,
              <SaveButton isLoading={isLoading} key="save" label={translate('app.buttons.save')} />,
            ]}
          />
        </Form>
      </RecordContextProvider>
    </Dialog>
  );
};
